import { useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import ComingSoon from "@/pages/ComingSoon";
import GeneralSettings from "@/pages/settings/GeneralSettings";
import APIKeysSettings from "@/pages/settings/APIKeysSettings";
import BrandingSettings from "@/pages/settings/BrandingSettings";
import OpenRouterStats from "@/pages/settings/OpenRouterStats";
import PaymentSettings from "@/pages/settings/PaymentSettings";
import CommunicationSettings from "@/pages/settings/CommunicationSettings";
import EmailConfiguration from "@/pages/settings/EmailConfiguration";
import ElevenLabsConfiguration from "@/pages/settings/ElevenLabsConfiguration";
import ApifySettings from "@/pages/settings/ApifySettings";
import ApifyActors from "@/pages/data/ApifyActors";
import ApifyActorDetailV2 from "@/pages/data/ApifyActorDetailV2";
import AIActors from "@/pages/ai/AIActors";
import AIActorView from "@/pages/ai/AIActorView";
import AIActorManage from "@/pages/ai/AIActorManage";
import AIActorChatConfig from "@/pages/ai/AIActorChatConfig";
import AIActorEmailConfig from "@/pages/ai/AIActorEmailConfig";
import AIActorPublicProfile from "@/pages/ai/AIActorPublicProfile";
import AIActorInquiryForm from "@/pages/ai/AIActorInquiryForm";
import AIActorModelsSettings from "@/pages/ai/AIActorModelsSettings";
import AIActorAutomationRules from "@/pages/ai/AIActorAutomationRules";
import AIActorDraftingConditions from "@/pages/ai/AIActorDraftingConditions";
import AIActorEmailTags from "@/pages/ai/AIActorEmailTags";
import AIActorOperationLogs from "@/pages/ai/AIActorOperationLogs";
import AIActorEmailKnowledgeBase from "@/pages/ai/AIActorEmailKnowledgeBase";
import AIActorEmails from "@/pages/ai/AIActorEmails";
import AIEmailSettings from "@/pages/ai/AIEmailSettings";
import EmailKnowledgeBase from "@/pages/ai/EmailKnowledgeBase";
import AIContacts from "@/pages/ai/AIContacts";
import AutomationRulesV2 from "@/pages/ai/AutomationRulesV2";
import AIActorFormPage from "@/pages/ai/AIActorFormPage";
import FormSubmissions from "@/pages/ai/FormSubmissions";
import ChatWidgetSettings from "@/pages/ai/ChatWidgetSettings";
import VoiceModels from "@/pages/ai/VoiceModels";
import VoiceModelSettings from "@/pages/ai/VoiceModelSettings";
import AIDashboard from "@/pages/ai/AIDashboard";
import AITasks from "@/pages/ai/AITasks";
import Leads from "@/pages/ai/Leads";
import AITraining from "@/pages/ai/AITraining";
import FeedbackDashboard from "@/pages/ai/FeedbackDashboard";
import KnowledgeBase from "@/pages/ai/KnowledgeBaseEnhanced";
import ChatModeration from "@/pages/ChatModeration";

export default function Dashboard({ setIsAuthenticated }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="dashboard">
      <Sidebar sidebarOpen={sidebarOpen} />
      
      <div className="flex-1 flex flex-col">
        <Header 
          sidebarOpen={sidebarOpen} 
          setSidebarOpen={setSidebarOpen}
          onLogout={handleLogout}
        />
        
        <main className="flex-1 overflow-y-auto">
          <Routes>
            <Route path="/settings/general" element={<GeneralSettings />} />
            <Route path="/settings/api-keys" element={<APIKeysSettings />} />
            <Route path="/settings/branding" element={<BrandingSettings />} />
            <Route path="/settings/openrouter-stats" element={<OpenRouterStats />} />
            <Route path="/settings/payment" element={<PaymentSettings />} />
            <Route path="/settings/communication" element={<CommunicationSettings />} />
            <Route path="/settings/communication/email/:configId" element={<EmailConfiguration />} />
            <Route path="/settings/communication/elevenlabs" element={<ElevenLabsConfiguration />} />
            <Route path="/settings/data/apify" element={<ApifySettings />} />
            <Route path="/data/apify-actors" element={<ApifyActors />} />
            <Route path="/data/apify-actors/:actorId" element={<ApifyActorDetailV2 />} />
            <Route path="/ai/actors" element={<AIActors />} />
            <Route path="/ai/actors/create" element={<AIActorFormPage />} />
            <Route path="/ai/actors/edit/:id" element={<AIActorFormPage />} />
            <Route path="/ai/actors/manage/:actorId" element={<AIActorManage />} />
            <Route path="/ai/actors/:actorId/chat-config" element={<AIActorChatConfig />} />
            <Route path="/ai/actors/:actorId/email-config" element={<AIActorEmailConfig />} />
            <Route path="/ai/actors/:actorId/models-settings" element={<AIActorModelsSettings />} />
            <Route path="/ai/actors/:actorId/automation-rules" element={<AIActorAutomationRules />} />
            <Route path="/ai/actors/:actorId/drafting-conditions" element={<AIActorDraftingConditions />} />
            <Route path="/ai/actors/:actorId/email-tags" element={<AIActorEmailTags />} />
            <Route path="/ai/actors/:actorId/operation-logs" element={<AIActorOperationLogs />} />
            <Route path="/ai/actors/:actorId/email-knowledge-base" element={<AIActorEmailKnowledgeBase />} />
            <Route path="/ai/actors/:actorId/public-profile" element={<AIActorPublicProfile />} />
            <Route path="/ai/actors/:actorId/inquiry-form" element={<AIActorInquiryForm />} />
            <Route path="/ai/actors/:actorId/form-submissions" element={<FormSubmissions />} />
            <Route path="/ai/actors/view/:actorId" element={<AIActorView />} />
            <Route path="/ai/actors/:actorId/emails" element={<AIActorEmails />} />
            <Route path="/ai/actors/:actorId/contacts" element={<AIContacts />} />
            <Route path="/ai/actors/:actorId/emails/settings" element={<AIEmailSettings />} />
            <Route path="/ai/actors/:actorId/emails/knowledge-base" element={<EmailKnowledgeBase />} />
            <Route path="/ai/actors/:actorId/automation-rules" element={<AutomationRulesV2 />} />
            <Route path="/ai/actors/:actorId/ai-tasks" element={<AITasks />} />
            <Route path="/ai/actors/:actorId/widget-settings" element={<ChatWidgetSettings />} />
            <Route path="/ai/voice-models" element={<VoiceModels />} />
            <Route path="/ai/voice-model-settings" element={<VoiceModelSettings />} />
            <Route path="/ai/dashboard" element={<AIDashboard />} />
            <Route path="/ai/feedback" element={<FeedbackDashboard />} />
            <Route path="/ai/knowledge-base" element={<KnowledgeBase />} />
            <Route path="/ai/tasks" element={<AITasks />} />
            <Route path="/ai/leads" element={<Leads />} />
            <Route path="/ai/scraping-projects" element={<ApifyActors />} />
            <Route path="/ai/training" element={<AITraining />} />
            <Route path="/chat-moderation" element={<ChatModeration />} />
            <Route path="/" element={<ComingSoon />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}